/**
 * features/hr/ClockingBoard.tsx
 * ------------------------------------------------------------------
 * HR DOMAIN — the Clocking screen: the sign-in sheet.
 *
 * Names down the left, Monday to Saturday across, and in each day the time
 * in, the time out and how late. By the week or by the month. Late days keep
 * the register's colours, from pale to deep, so a pattern still stands out;
 * a day missing a clock-in or clock-out is outlined and says which. Totals on
 * the right cover the days shown, and the rows sort by them. Hover a day for
 * its details; click a row to open that person's profile (HR only).
 *
 * HR sees it, and so does the clocking uploader (Makoni's account, Nov 2026),
 * who has no other HR access: no profiles, no Start times, and approved leave
 * dates only (useClockingData).
 *
 * PDF AND CSV (Nov 2026) download what is on screen: the week or month, staff
 * type, search and sort. A month's PDF is two halves or all on one page, which
 * the person printing chooses (lib/clockingExport).
 *
 * THE WHOLE SCREEN (Nov 2026), as a scheme of work opens: the layer sits over
 * the sidebar and top bar, because the sheet wants the width, and Back leaves it.
 *
 * Chosen by the school in Nov 2026, replacing a month of coloured squares
 * (Oct 2026) whose times could only be read by hovering, which itself
 * replaced a day timeline that answered one day at a time. Rows stay tall
 * enough to read: there is always room to scroll.
 *
 * Every cell is worked out from the punches against the school's start times
 * and the live public-holiday list (lib/clockingRules, lib/clockingRegister),
 * never the status stored at upload. Rows are the staff who clocked at least
 * once in the month or months shown, or were on leave in them, so someone away
 * all week still has a row. Sundays are not shown.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { ArrowLeft, Clock, ChevronLeft, ChevronRight, Info, Upload, Search, Timer } from 'lucide-react';
import { ExportBar, GradientHero, SoftCard, AccentButton, glassFieldClass } from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';
import { useClockingData } from '@/features/hr/useHrData';
import { useClockingRules } from '@/features/hr/useClockingRules';
import ClockingStartTimes from '@/features/hr/ClockingStartTimes';
import { useAuth } from '@/contexts/AuthContext';
import { fmtMinutes, summarise, weekday, type PeriodSummary } from '@/lib/clockingRules';
import {
  addDays, cellTitle, monthDays, monthSheetDays, onLeaveOn, registerCell, shiftMonth, shortLate,
  weekDays, weekStart, type CellKind, type RegisterCell,
} from '@/lib/clockingRegister';
import { downloadCsv } from '@/lib/tableExport';
import {
  clockingCsvColumns, MONTH_PDF_LAYOUT_LABELS, type ClockingExportRow, type MonthPdfLayout,
} from '@/lib/clockingExport';
import { buildClockingPdf } from '@/lib/clockingPdf';
import {
  staffFullName, STAFF_CATEGORY_LABELS, type HrAttendance, type HrStaff, type StaffCategory,
} from '@/lib/hr';

const CATEGORY_ORDER: StaffCategory[] = ['teaching', 'management', 'admin', 'support', 'ancillary'];
const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DOW_LETTER = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/** Day colours. Late shades run from pale to deep so "how late" reads at a glance. */
const CELL: Record<CellKind, string> = {
  off: 'bg-gray-100 text-gray-400 dark:bg-gray-800/60 dark:text-gray-500',
  notYet: 'border border-dashed border-gray-300 text-gray-400 dark:border-gray-600 dark:text-gray-500',
  leave: 'bg-blue-100 text-blue-700 font-bold dark:bg-blue-900/50 dark:text-blue-200',
  noPunch: 'bg-red-100 text-red-700 font-bold dark:bg-red-900/50 dark:text-red-200',
  onTime: '',
  late15: 'bg-amber-200 text-amber-950 dark:bg-amber-800/60 dark:text-amber-50',
  late45: 'bg-orange-400 text-orange-950 dark:bg-orange-600 dark:text-white',
  lateMore: 'bg-orange-700 text-white dark:bg-orange-500 dark:text-orange-950',
};
const MISSING = 'ring-1 ring-inset ring-red-500';

type Sort = 'late' | 'missing' | 'name';
type View = 'week' | 'month';

interface RegisterRow {
  staff: HrStaff;
  cells: RegisterCell[];
  totals: PeriodSummary;
  noPunchDays: number;
}

interface Props {
  /** Leaves the full-screen sheet. The sidebar is hidden while it is open. */
  onBack: () => void;
  /** Absent for the clocking uploader, who has no access to staff profiles. */
  onOpenStaff?: (staffId: string) => void;
  /** Opens the upload screen — its own route, so a reminder can link to it.
   *  Absent for HR staff: the upload belongs to one account and system admin. */
  onImport?: () => void;
}

function shortDate(date: string, opts: Intl.DateTimeFormatOptions): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString([], { ...opts, timeZone: 'UTC' });
}

/** "5 Oct", without the leading zero some computers' date settings add. */
function dayMonth(date: string, month: 'short' | 'long' = 'short'): string {
  return `${Number(date.slice(8))} ${shortDate(date, { month })}`;
}

function monthLabel(month: string): string {
  return shortDate(`${month}-15`, { month: 'long', year: 'numeric' });
}

function weekLabel(days: string[]): string {
  const first = days[0];
  const last = days[days.length - 1];
  const from = first.slice(5, 7) === last.slice(5, 7) ? String(Number(first.slice(8))) : dayMonth(first);
  return `${from} – ${dayMonth(last)} ${last.slice(0, 4)}`;
}

/** The month view's weeks, for the row above the dates. */
function weekGroups(days: string[]): { start: string; label: string; span: number }[] {
  const out: { start: string; label: string; span: number }[] = [];
  for (const d of days) {
    const cur = out[out.length - 1];
    if (cur && weekStart(cur.start) === weekStart(d)) cur.span += 1;
    else out.push({ start: d, label: '', span: 1 });
  }
  for (const g of out) {
    const end = addDays(g.start, g.span - 1);
    g.label = g.span === 1
      ? dayMonth(end)
      : `${Number(g.start.slice(8))}–${dayMonth(end)}`;
  }
  return out;
}

export default function ClockingBoard({ onBack, onOpenStaff, onImport }: Props) {
  const data = useClockingData();
  const rules = useClockingRules();
  const { permissions } = useAuth();
  const [editingStarts, setEditingStarts] = useState(false);

  /** The last day uploaded. Days after it are "not yet", not absent. */
  const lastUploaded = useMemo(() => {
    let d = '';
    for (const a of data.attendance) if (a.punches.length && a.date > d) d = a.date;
    return d;
  }, [data.attendance]);

  const [view, setView] = useState<View>('week');
  /** Any date in the week or month being looked at; until one is picked, the last uploaded. */
  const [picked, setPicked] = useState<string | null>(null);
  const anchor = picked ?? (lastUploaded || new Date().toISOString().slice(0, 10));
  const monday = weekStart(anchor);
  const month = anchor.slice(0, 7);
  const [sort, setSort] = useState<Sort>('name');
  const [cat, setCat] = useState<'all' | StaffCategory>('teaching');
  const [query, setQuery] = useState('');
  const [monthLayout, setMonthLayout] = useState<MonthPdfLayout>('halves');

  const days = useMemo(() => (view === 'week' ? weekDays(monday) : monthSheetDays(month)), [view, monday, month]);
  const period = view === 'week' ? weekLabel(days) : monthLabel(month);

  const canGoNext = view === 'week'
    ? !lastUploaded || addDays(monday, 7) <= lastUploaded
    : !lastUploaded || shiftMonth(month, 1) <= lastUploaded.slice(0, 7);

  const step = (by: 1 | -1) => setPicked(view === 'week' ? addDays(monday, by * 7) : `${shiftMonth(month, by)}-01`);

  /** A week becomes its month; a month opens on its last uploaded week, or its first working one. */
  const switchView = (next: View) => {
    if (next === view) return;
    if (next === 'month') setPicked(addDays(monday, 2));
    else if (lastUploaded.slice(0, 7) === month) setPicked(lastUploaded);
    else setPicked(monthSheetDays(month)[0]);
    setView(next);
  };

  const rows: RegisterRow[] = useMemo(() => {
    // Who has a row is decided on whole months, so a week off does not hide anyone.
    const span = [...new Set(days.map((d) => d.slice(0, 7)))].flatMap(monthDays);
    const first = span[0];
    const last = span[span.length - 1];
    const inSpan = new Map<string, Map<string, HrAttendance>>();
    for (const a of data.attendance) {
      if (a.date < first || a.date > last) continue;
      const m = inSpan.get(a.staffId) ?? inSpan.set(a.staffId, new Map()).get(a.staffId)!;
      m.set(a.date, a);
    }
    const out: RegisterRow[] = [];
    for (const s of data.staff) {
      if (s.status === 'exited') continue;
      const recs = inSpan.get(s.id);
      const clocked = !!recs && [...recs.values()].some((r) => r.punches.length);
      if (!clocked && !span.some((d) => onLeaveOn(data.leave, s.id, d))) continue; // not on the terminals these months
      const own = rules.rulesFor(s.category); // this staff type's start times
      const cells = days.map((d) => registerCell(d, recs?.get(d), { lastUploaded, onLeave: onLeaveOn(data.leave, s.id, d), rules: own }));
      const showing = days.flatMap((d) => { const r = recs?.get(d); return r ? [{ ...r }] : []; });
      out.push({
        staff: s,
        cells,
        totals: summarise(showing, own),
        noPunchDays: cells.filter((c) => c.kind === 'noPunch').length,
      });
    }
    return out;
  }, [days, data.attendance, data.staff, data.leave, lastUploaded, rules]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const byName = (a: RegisterRow, b: RegisterRow) => staffFullName(a.staff).localeCompare(staffFullName(b.staff));
    return rows
      .filter((r) => (cat === 'all' || r.staff.category === cat)
        && (!q || `${staffFullName(r.staff)} ${r.staff.jobTitle ?? ''}`.toLowerCase().includes(q)))
      .sort((a, b) => (sort === 'late'
        ? b.totals.lateMinutes - a.totals.lateMinutes || b.totals.lateDays - a.totals.lateDays
        : sort === 'missing'
          ? b.totals.missingPunches - a.totals.missingPunches
          : 0) || byName(a, b));
  }, [rows, cat, query, sort]);

  const periodTotals = useMemo(() => shown.reduce((t, r) => ({
    lateDays: t.lateDays + r.totals.lateDays,
    missing: t.missing + r.totals.missingPunches,
    noPunch: t.noPunch + r.noPunchDays,
  }), { lateDays: 0, missing: 0, noPunch: 0 }), [shown]);

  /* What the downloads carry: the rows on screen, in the order on screen. */
  const exportRows: ClockingExportRow[] = shown.map((r) => ({
    name: staffFullName(r.staff),
    jobTitle: r.staff.jobTitle ?? '',
    cells: r.cells,
    totals: r.totals,
    noPunchDays: r.noPunchDays,
  }));
  const fileName = `LKC-clocking-${view === 'week' ? monday : month}`;
  const scope = [
    cat === 'all' ? 'All staff types' : STAFF_CATEGORY_LABELS[cat],
    sort === 'name' ? 'A to Z' : sort === 'late' ? 'most late first' : 'most missed punches first',
    ...(query.trim() ? [`matching "${query.trim()}"`] : []),
  ].join(', ');
  const downloadPdf = () => buildClockingPdf({
    view,
    period,
    days,
    rows: exportRows,
    scope,
    uploadedTo: lastUploaded ? dayMonth(lastUploaded, 'long') : 'nothing yet',
    monthLayout,
    isHoliday: (d) => !!rules.holidayName(d),
  }).save(`${fileName}.pdf`);
  const downloadSheet = () => downloadCsv({
    title: 'Staff clocking',
    filename: fileName,
    columns: clockingCsvColumns(days, rules.holidayName),
    rows: exportRows,
  });

  const isWeekStart = (d: string, i: number) => view === 'month' && (i === 0 || weekday(d) === 1);
  const weekLine = 'border-l border-gray-300 dark:border-gray-600';

  return (
    /* Above the sidebar (z-40) and top bar, below the session-timeout dialog (z-110). */
    <div className="fixed inset-0 z-[90] overflow-y-auto bg-gray-50 dark:bg-gray-900">
    <div className="space-y-5 p-4 sm:p-6">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
      ><ArrowLeft size={16} /> Back to the portal</button>
      <GradientHero
        icon={Clock}
        title="Clocking"
        subtitle={lastUploaded ? `${period} · uploaded to ${dayMonth(lastUploaded, 'long')}` : 'No clocking uploaded yet'}
        actions={(onImport || permissions.canAccessHR) && (
          <>
            {permissions.canAccessHR && <AccentButton icon={Timer} variant="soft" onClick={() => setEditingStarts(true)}>Start times</AccentButton>}
            {onImport && <AccentButton icon={Upload} onClick={onImport}>Upload clocking</AccentButton>}
          </>
        )}
      />

      {data.loading ? (
        <div className="flex justify-center py-16"><Spinner label="Loading timesheets…" /></div>
      ) : (
        <>
          {/* Week or month, sort, filters */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden" role="group" aria-label="View">
                {([['week', 'Week'], ['month', 'Month']] as const).map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => switchView(k)}
                    aria-pressed={view === k}
                    className={`px-4 py-1.5 text-sm font-semibold border-r last:border-r-0 border-gray-200 dark:border-gray-600 ${view === k
                      ? 'bg-[var(--accent-strong)] text-white' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200'}`}
                  >{label}</button>
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => step(-1)} className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800" aria-label={`Previous ${view}`}><ChevronLeft size={16} /></button>
                <span className="min-w-[11.5rem] text-center font-semibold text-gray-800 dark:text-gray-100">{period}</span>
                <button
                  onClick={() => step(1)}
                  disabled={!canGoNext}
                  className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40" aria-label={`Next ${view}`}
                ><ChevronRight size={16} /></button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden" role="group" aria-label="Sort">
                {([['name', 'A–Z'], ['late', 'Most late'], ['missing', 'Most missed punches']] as const).map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => setSort(k)}
                    aria-pressed={sort === k}
                    className={`px-3 py-1.5 text-xs font-semibold border-r last:border-r-0 border-gray-200 dark:border-gray-600 ${sort === k
                      ? 'bg-[var(--accent-strong)] text-white' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200'}`}
                  >{label}</button>
                ))}
              </div>
              <select
                value={cat}
                onChange={(e) => setCat(e.target.value as 'all' | StaffCategory)}
                className="text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-1.5"
                aria-label="Staff type"
              >
                <option value="all">All staff types</option>
                {CATEGORY_ORDER.map((c) => <option key={c} value={c}>{STAFF_CATEGORY_LABELS[c]}</option>)}
              </select>
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a person" className={glassFieldClass('py-1 pl-8 w-44 text-xs')} />
              </div>
              {view === 'month' && (
                <select
                  value={monthLayout}
                  onChange={(e) => setMonthLayout(e.target.value as MonthPdfLayout)}
                  className="text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-1.5"
                  aria-label="Month PDF layout"
                  title="How the month's PDF is laid out"
                >
                  {(Object.keys(MONTH_PDF_LAYOUT_LABELS) as MonthPdfLayout[]).map((k) => (
                    <option key={k} value={k}>PDF: {MONTH_PDF_LAYOUT_LABELS[k]}</option>
                  ))}
                </select>
              )}
              <ExportBar disabled={shown.length === 0} onPdf={downloadPdf} onCsv={downloadSheet} label="Download" />
            </div>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            {shown.length} staff · {periodTotals.lateDays} late arrivals · {periodTotals.missing} days missing a punch · {periodTotals.noPunch} days with no punch.
            {' '}Late is any minute after each staff type’s start ({CATEGORY_ORDER.map((c) => {
              const r = rules.rulesFor(c);
              return `${STAFF_CATEGORY_LABELS[c]} ${r.weekdayStart}${r.saturdayStart ? ` / Sat ${r.saturdayStart}` : ''}`;
            }).join(' · ')}), in term time only: public holidays and the school holidays have no start.
          </p>

          {/* The sheet */}
          <SoftCard pad="p-0" hover={false}>
            <div className="overflow-x-auto">
              <table className="border-separate border-spacing-0 text-xs tabular-nums">
                <thead>
                  {view === 'month' && (
                    <tr>
                      <th className="sticky left-0 z-10 bg-white dark:bg-gray-800 border-r border-gray-300 dark:border-gray-600" />
                      {weekGroups(days).map((g) => (
                        <th key={g.start} colSpan={g.span} className={`px-1 pt-2 text-center text-[11px] font-medium text-gray-400 whitespace-nowrap ${weekLine}`}>{g.label}</th>
                      ))}
                      <th colSpan={4} className={weekLine} />
                    </tr>
                  )}
                  <tr>
                    <th className="sticky left-0 z-10 bg-white dark:bg-gray-800 border-r border-b border-gray-200 dark:border-gray-600 text-left font-semibold text-gray-500 px-3 py-2 min-w-[170px]">Staff</th>
                    {days.map((d, i) => {
                      const hol = rules.holidayName(d);
                      const dow = weekday(d);
                      const term = rules.inTerm ? rules.inTerm(d) : true;
                      return (
                        <th
                          key={d}
                          title={hol ?? (term ? undefined : 'School holidays')}
                          className={`py-2 text-center font-semibold leading-tight border-b border-gray-200 dark:border-gray-600 ${isWeekStart(d, i) ? weekLine : ''} ${hol || !term ? 'text-gray-300 dark:text-gray-600' : 'text-gray-700 dark:text-gray-200'}`}
                        >
                          <span className="block text-[13px]">{view === 'week' ? DOW_SHORT[dow] : DOW_LETTER[dow]}</span>
                          <span className="block text-[11px] font-medium">{view === 'week' ? dayMonth(d) : Number(d.slice(8))}</span>
                        </th>
                      );
                    })}
                    <th className={`px-2.5 text-right font-semibold text-gray-500 leading-tight border-b border-gray-200 dark:border-gray-600 ${weekLine}`}>Days<br />late</th>
                    <th className="px-2.5 text-right font-semibold text-gray-500 leading-tight border-b border-gray-200 dark:border-gray-600">Time<br />late</th>
                    <th className="px-2.5 text-right font-semibold text-gray-500 leading-tight border-b border-gray-200 dark:border-gray-600">Missed<br />punch</th>
                    <th className="px-2.5 text-right font-semibold text-gray-500 leading-tight border-b border-gray-200 dark:border-gray-600">No<br />punch</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => (
                    <tr key={r.staff.id} className={`group ${onOpenStaff ? 'cursor-pointer' : ''}`} onClick={onOpenStaff && (() => onOpenStaff(r.staff.id))}>
                      <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-gray-700 border-r border-b border-gray-200 dark:border-gray-600 px-3 py-2 whitespace-nowrap">
                        <span className="block font-semibold text-gray-800 dark:text-gray-100 text-[13px]">{staffFullName(r.staff)}</span>
                        <span className="block text-[11px] text-gray-400 truncate max-w-[170px]">{r.staff.jobTitle}</span>
                      </td>
                      {r.cells.map((c, i) => (
                        <td
                          key={days[i]}
                          title={cellTitle(days[i], c, rules.holidayName(days[i]))}
                          className={`p-0.5 border-b border-gray-200 dark:border-gray-700 group-hover:bg-gray-50 dark:group-hover:bg-gray-700/40 ${isWeekStart(days[i], i) ? weekLine : ''}`}
                        >
                          <DayCell cell={c} view={view} isHoliday={!!rules.holidayName(days[i])} />
                        </td>
                      ))}
                      <td className={`px-2.5 text-right border-b border-gray-200 dark:border-gray-700 ${weekLine}`}>{r.totals.lateDays || '—'}</td>
                      <td className="px-2.5 text-right whitespace-nowrap border-b border-gray-200 dark:border-gray-700">{r.totals.lateMinutes ? fmtMinutes(r.totals.lateMinutes) : '—'}</td>
                      <td className="px-2.5 text-right border-b border-gray-200 dark:border-gray-700">{r.totals.missingPunches || '—'}</td>
                      <td className="px-2.5 text-right border-b border-gray-200 dark:border-gray-700">{r.noPunchDays || '—'}</td>
                    </tr>
                  ))}
                  {shown.length === 0 && (
                    <tr><td colSpan={days.length + 5} className="px-3 py-6 text-sm text-gray-400">No clocking for {period}.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-2 px-4 py-3 border-t border-gray-100 dark:border-gray-700/70 text-[11px] text-gray-500 dark:text-gray-400">
              <Legend cls={CELL.late15} label="In up to 15 min late" />
              <Legend cls={CELL.late45} label="16–45 min late" />
              <Legend cls={CELL.lateMore} label="Over 45 min late" />
              <Legend cls={MISSING} label="Missing a clock-in or clock-out" />
              <Legend cls={CELL.noPunch} label="No punch on a working day" />
              <Legend cls={CELL.leave} label="Leave" />
              <Legend cls={CELL.off} label="Public holiday (PH), school holiday or Saturday not in" />
              <Legend cls={CELL.notYet} label="Not uploaded yet" />
            </div>
          </SoftCard>

          <ClockingStartTimes open={editingStarts} onClose={() => setEditingStarts(false)} starts={rules.starts} />

          <div className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
            <Info size={14} className="shrink-0 mt-0.5" />
            <p>
              Each day shows the first punch and the last; hover a day to see every punch.{onOpenStaff && ' Click a name to open the person’s profile.'}
              {' '}Punches arrive from the clocking terminals' weekly upload. The terminals match fingerprints themselves
              and export only an ID and the times, so no biometric data is stored in the platform.
            </p>
          </div>
        </>
      )}
    </div>
    </div>
  );
}

/** One day: in, out and how late, each on its own line so a row's times line up. */
function DayCell({ cell, view, isHoliday }: { cell: RegisterCell; view: View; isHoliday: boolean }) {
  const size = view === 'week' ? 'w-[124px] min-h-[76px]' : 'w-[66px] min-h-[76px]';
  const v = cell.v;
  if (!v) {
    const word = cell.kind === 'leave' ? 'Leave'
      : cell.kind === 'noPunch' ? 'No punch'
        : cell.kind === 'off' ? (isHoliday ? 'PH' : '–')
          : view === 'week' ? 'Not uploaded' : '';
    return <div className={`${size} rounded-md flex items-center justify-center text-center text-[11px] leading-tight px-1 ${CELL[cell.kind]}`}>{word}</div>;
  }
  const late = view === 'week' ? `${fmtMinutes(v.lateMinutes)} late` : shortLate(v.lateMinutes);
  const shaded = cell.kind !== 'onTime';
  const gap = `text-[11px] font-bold ${shaded ? '' : 'text-red-700 dark:text-red-300'}`;
  return (
    <div className={`${size} rounded-md flex flex-col justify-center gap-0.5 px-1 py-1.5 text-center leading-snug ${CELL[cell.kind]} ${cell.missing ? MISSING : ''}`}>
      <TimeLine label="In" view={view} value={v.firstIn} valueClass="font-bold text-[13px]" gap="no in" gapClass={gap} />
      <TimeLine label="Out" view={view} value={v.lastOut} valueClass={`text-[13px] ${shaded ? '' : 'text-gray-600 dark:text-gray-300'}`} gap="no out" gapClass={gap} />
      {v.late && <span className="text-[11px] font-bold whitespace-nowrap">{late}</span>}
    </div>
  );
}

function TimeLine({ label, view, value, valueClass, gap, gapClass }: {
  label: string; view: View; value: string | null; valueClass: string; gap: string; gapClass: string;
}) {
  const text = value
    ? <span className={valueClass}>{value}</span>
    : <span className={gapClass}>{gap}</span>;
  if (view === 'month') return <span className="block">{text}</span>;
  return (
    <span className="flex items-baseline justify-center gap-2">
      <span className="w-6 text-left text-[11px] opacity-70">{label}</span>
      <span className="w-12 text-left">{text}</span>
    </span>
  );
}

function Legend({ cls, label }: { cls: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block w-3.5 h-3.5 rounded-sm ${cls}`} />
      {label}
    </span>
  );
}
